import {
    PauseCircleOutlined,
    PlayCircleOutlined,
    PlusOutlined
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Dropdown, Flex } from "antd";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../context/AppContext";
import HSelect from "../../components/custom/input/select/Select";
import HTableWithTabs from "../../components/custom/table/HTableWithTabs";
import { DeleteComponent, EmptyListView } from "../utils/CommonVessels";
import Input from "../../components/custom/input/input/Input";
import { COMBINED_FILTER_OPTIONS, type UseWorkflowsResult, type WorkflowRecord } from "./hooks/useWorkflows";

export type { WorkflowRecord, WorkflowApiRecord, WorkflowTriggerType } from "./hooks/useWorkflows";

export interface TriggerRulesListProps {
    workflows: UseWorkflowsResult;
    onWorkflowClick: (workflow: WorkflowRecord) => void;
    onNewWorkflow: () => void;
    /** When set, auto-select workflow matching this id once data is loaded */
    initialWorkflowId?: string | null;
}

const TriggerRulesList: React.FC<TriggerRulesListProps> = ({
    workflows,
    onWorkflowClick,
    onNewWorkflow,
    initialWorkflowId,
}) => {
    const [appProperties] = useContext(AppContext);
    const {
        requestWorkflows,
        activeTab,
        searchInputValue,
        setSearchInputValue,
        appliedSearchKeyword,
        triggerTypeFilter,
        statusFilter,
        combinedFilterValue,
        handleFilterChange,
        handleSearchApply,
        handleSearchClear,
        handleTabChange,
        enableWorkflow,
        disableWorkflow,
        deleteWorkflow,
        tableRef,
        currentPageData,
    } = workflows;

    const [deleteModalRecord, setDeleteModalRecord] = useState<WorkflowRecord | null>(null);
    const hasRestoredFromUrl = useRef(false);

    useEffect(() => {
        if (
            initialWorkflowId &&
            initialWorkflowId !== "new" &&
            currentPageData.length > 0 &&
            !hasRestoredFromUrl.current
        ) {
            const match = currentPageData.find((w) => w.workflowId === initialWorkflowId);
            if (match) {
                hasRestoredFromUrl.current = true;
                onWorkflowClick(match);
            }
        }
    }, [initialWorkflowId, currentPageData, onWorkflowClick]);

    const handleDisable = useCallback(
        (record: WorkflowRecord, e: React.MouseEvent) => {
            e?.stopPropagation?.();
            disableWorkflow(record);
        },
        [disableWorkflow]
    );

    const handleEnable = useCallback(
        (record: WorkflowRecord, e: React.MouseEvent) => {
            e?.stopPropagation?.();
            enableWorkflow(record);
        },
        [enableWorkflow]
    );

    const handleDeleteClick = useCallback((record: WorkflowRecord, e: React.MouseEvent) => {
        e?.stopPropagation?.();
        setDeleteModalRecord(record);
    }, []);

    const handleDeleteConfirm = useCallback(
        async (record: WorkflowRecord) => {
            await deleteWorkflow(record);
            setDeleteModalRecord(null);
        },
        [deleteWorkflow]
    );

    const getActionsMenuItems = useCallback(
        (record: WorkflowRecord): MenuProps["items"] => {
            const isPredefined = activeTab === "predefined";
            if (isPredefined) {
                return [];
            }

            const isPaused =
                (record.status ?? "").toLowerCase() === "paused" || (record.status ?? "").toLowerCase() === "draft";
            const items: MenuProps["items"] = [];

            const stopAnd =
                (fn: (e: React.MouseEvent) => void) =>
                (info: { domEvent: React.MouseEvent | React.KeyboardEvent }) => {
                    info.domEvent.stopPropagation();
                    fn(info.domEvent as React.MouseEvent);
                };

            if (isPaused) {
                items.push({
                    key: "enable",
                    label: "Enable",
                    icon: <PlayCircleOutlined className="pe-3" />,
                    onClick: stopAnd((e) => handleEnable(record, e)),
                });
            } else {
                items.push({
                    key: "disable",
                    label: "Disable",
                    icon: <PauseCircleOutlined className="pe-3" />,
                    onClick: stopAnd((e) => handleDisable(record, e)),
                });
            }

            items.push({
                key: "delete",
                label: "Delete",
                icon: <div className="actionIconsSprite deleteTemplate-icon" />,
                danger: true,
                onClick: stopAnd((e) => handleDeleteClick(record, e)),
            });

            return items;
        },
        [activeTab, handleDisable, handleEnable, handleDeleteClick]
    );

    const columns = [
        {
            title: "Workflow Name",
            dataIndex: "workflowName",
            key: "workflowName",
            width: "24%",
            render: (text: string) => <span className="trigger-rules-list-name">{text}</span>,
        },
        {
            title: "Trigger Type",
            dataIndex: "triggerType",
            key: "triggerType",
            width: "16%",
            render: (text: string): React.ReactNode => {
                const parts = (text ?? "").split(".");
                const middle = parts.length >= 2 ? parts[1] : text;
                return <span className="trigger-rules-list-cell-text">{middle}</span>;
            },
        },
        {
            title: "Direction",
            dataIndex: "direction",
            key: "direction",
            width: "16%",
            render: (direction: WorkflowRecord["direction"]) => (
                <span
                    className={`trigger-rules-list-direction d-inline-flex align-items-center gap-1 rounded-5 trigger-rules-list-direction-${direction.toLowerCase()}`}
                >
                    {direction === "Incoming" ? (
                        <div className="automationIconsSprite incoming-drirection-icon"></div>
                    ) : (
                        <div className="automationIconsSprite outgoing-direction-icon"></div>
                    )}
                    {direction}
                </span>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: "14%",
            render: (status: string) => (
                <span
                    className={`trigger-rules-list-status d-inline-flex align-items-center gap-1 trigger-rules-list-status-${(status ?? "").toLowerCase()}`}
                >
                    <span className="trigger-rules-list-status-dot" />
                    {status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : status}
                </span>
            ),
        },
        {
            title: "Stats",
            dataIndex: "runs",
            key: "runs",
            width: "20%",
            render: (runs?: number) => (
                <span className="trigger-rules-list-runs d-inline-flex align-items-baseline gap-1">
                    <span className="trigger-rules-list-runs-count">{(runs ?? 0).toLocaleString()}</span>
                    <span className="trigger-rules-list-runs-label">runs</span>
                </span>
            ),
        },
        ...(activeTab !== "predefined"
            ? [
                  {
                      title: "",
                      dataIndex: "workflowId",
                      key: "actions",
                      width: "10%",
                      align: "right" as const,
                      render: (_: unknown, record: WorkflowRecord): React.ReactNode => {
                          const menuItems = getActionsMenuItems(record);
                          if (!menuItems || menuItems.length === 0) {
                              return null;
                          }
                          return (
                              <Dropdown
                                  menu={{
                                      items: menuItems,
                                      onClick: (info) => info.domEvent.stopPropagation(),
                                  }}
                                  trigger={["click"]}
                                  placement="bottomRight"
                                  rootClassName="editDeleteSettingDropdown"
                              >
                                  <div
                                      style={{ cursor: "pointer" }}
                                      className="actionIconsSprite editDeleteSettingsIcon"
                                      onClick={(e) => e.stopPropagation()}
                                  ></div>
                              </Dropdown>
                          );
                      },
                  },
              ]
            : []),
    ];

    const hTableWithTabsProps = {
        tableRef,
        tabMenuItems: [
            { key: "custom", label: "Custom" },
            { key: "predefined", label: "Pre-Defined Flows" },
        ] as const,
        defaultActiveKey: activeTab,
        onChange: handleTabChange,
        heightToBeSubtractedFromContainerHeight: 100,
        ui: {
            columns,
            rowKey: "workflowId",
            title: (
                <Flex align="center" gap={20}>
                    <HSelect
                        options={COMBINED_FILTER_OPTIONS}
                        value={combinedFilterValue}
                        onChange={handleFilterChange}
                        placeholder="Filter by trigger & status"
                        customProps={{ className: "mt-2", style: { width: 200 } }}
                    />
                    <Input
                        placeholder="Search by name"
                        allowClear
                        style={{ width: 200 }}
                        value={searchInputValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSearchInputValue(e.target.value);
                            if (!e.target.value?.trim()) {
                                handleSearchClear();
                            }
                        }}
                        onPressEnter={handleSearchApply}
                    />
                </Flex>
            ),
        },
        data: {
            request: requestWorkflows,
            requestExtraParams: {
                searchKeyword: appliedSearchKeyword,
                flowType: activeTab,
                triggerType: triggerTypeFilter,
                status: statusFilter,
            },
        },
        toolbar: {
            buttonText: "New Trigger Rule",
            onButtonClick: activeTab === "custom" ? onNewWorkflow : undefined,
            buttonIcon: <PlusOutlined />,
        },
        proProps: {
            locale: {
                emptyText: (
                    <EmptyListView
                        icon=""
                        tagLineOne="No workflows yet"
                        tagLineTwo="Create your first trigger rule automation"
                        tagHeight="32vh"
                    />
                ),
            },
        },
        onRowClick: (record: WorkflowRecord) => ({
            onClick: () => onWorkflowClick(record),
            style: { cursor: "pointer" },
        }),
    };

    return (
        <Flex className="trigger-rules-list trigger-rules-list-tabs flex-column h-100 overflow-hidden rounded-2 p-3">
            <HTableWithTabs {...hTableWithTabsProps} />

            {deleteModalRecord && (
                <DeleteComponent
                    modalOpen={!!deleteModalRecord}
                    setModalOpen={(open: boolean) => !open && setDeleteModalRecord(null)}
                    record={deleteModalRecord}
                    confirmDeleteIcon="hs-delete-icon"
                    confirmDeleteBtnName="Delete"
                    confirmDeleteComponentTitle="Delete workflow?"
                    confirmDeleteComponent="Are you sure you want to delete this workflow? This action cannot be undone."
                    appProperties={appProperties}
                    handleDelete={() => handleDeleteConfirm(deleteModalRecord)}
                />
            )}
        </Flex>
    );
};

export default TriggerRulesList;
